import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PrimaryGrid } from '@ui/components/common/Grid';
import SearchFilter from '@ui/components/Button/SearchFilter';
import { Export } from 'iconsax-react';
import PassportExportResponsibilityDialog from './PassportExportResponsibilityDialog';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { fetchFamilyFestPassportsAsync } from '@application/features/familyFest/familyFestPassportThunk';
import familyFestApi from '@infrastructure/family-fest-api';
import dayjs from 'dayjs';
import { HDatePicker } from '@ui/components/common/Pickers';
const PassportContent = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const { passports, isNdaConfirmed } = useAppSelector((state) => state.familyFestPassport);
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [searchValue, setSearchValue] = useState('');
    const [filters, setFilters] = useState({});
    const [resetFiltersKey, setResetFiltersKey] = useState(0);
    const [gridFilterOpen, setGridFilterOpen] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [exportSelection, setExportSelection] = useState('');
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [exportConsent, setExportConsent] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    useEffect(() => {
        if (eventId && isNdaConfirmed) {
            const sort = sortModel[0];
            dispatch(fetchFamilyFestPassportsAsync({
                eventId,
                pageNumber: paginationModel.page + 1,
                pageSize: paginationModel.pageSize,
                searchString: searchValue || undefined,
                firstName: filters.firstName,
                middleName: filters.middleName,
                lastName: filters.lastName,
                dateOfBirth: filters.dateOfBirth,
                passportNumber: filters.passportNumber,
                issueDate: filters.issueDate,
                expiryDate: filters.expiryDate,
                nationality: filters.nationality,
                sortBy: sort?.field,
                sortOrder: sort?.sort ?? 'desc'
            }));
        }
    }, [
        dispatch,
        eventId,
        paginationModel.page,
        paginationModel.pageSize,
        searchValue,
        filters,
        isNdaConfirmed,
        sortModel,
        selectedCompanyIds
    ]);
    const formatDate = (date) => {
        if (!date)
            return '—';
        const d = dayjs(date);
        return d.isValid() ? d.format('DD MMM YYYY') : '—';
    };
    const columns = useMemo(() => [
        {
            field: 'firstName',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.firstName'),
            flex: 1,
            minWidth: 130
        },
        {
            field: 'middleName',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.middleName'),
            flex: 1,
            minWidth: 130
        },
        {
            field: 'lastName',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.lastName'),
            flex: 1,
            minWidth: 130
        },
        {
            field: 'dateOfBirth',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.dateOfBirth'),
            flex: 1,
            minWidth: 130,
            renderCell: (params) => formatDate(params.value)
        },
        {
            field: 'passportNumber',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.passportNumber'),
            flex: 1,
            minWidth: 120
        },
        {
            field: 'issueDate',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.issueDate'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => formatDate(params.value)
        },
        {
            field: 'expiryDate',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.expiryDate'),
            flex: 1,
            minWidth: 120,
            renderCell: (params) => formatDate(params.value)
        },
        {
            field: 'nationality',
            type: 'string',
            headerName: t('events.eventDetails.passportTab.nationality'),
            flex: 1,
            minWidth: 120
        }
    ], [t]);
    const customFilterRender = useMemo(() => ({
        dateOfBirth: (value, onChange) => (_jsx(HDatePicker, { label: t('events.eventDetails.passportTab.dateOfBirth'), bg: 'grey8', value: value, handleSelectedDate: onChange, maxDate: new Date() })),
        issueDate: (value, onChange) => (_jsx(HDatePicker, { label: t('events.eventDetails.passportTab.issueDate'), bg: 'grey8', value: value, handleSelectedDate: onChange })),
        expiryDate: (value, onChange) => (_jsx(HDatePicker, { label: t('events.eventDetails.passportTab.expiryDate'), bg: 'grey8', value: value, handleSelectedDate: onChange }))
    }), [t]);
    const rows = useMemo(() => passports?.items || [], [passports]);
    const handleCloseExportDialog = () => {
        setExportDialogOpen(false);
        setExportConsent(false);
    };
    const handleConfirmExport = async () => {
        if (!exportConsent || !eventId)
            return;
        setIsExporting(true);
        try {
            await familyFestApi.downloadFamilyFestPassportsExport({
                eventId,
                exportFormat: 'excel',
                selectedIds: selectedRows
            });
            handleCloseExportDialog();
        }
        catch (error) {
            console.error('Export failed:', error);
        }
        finally {
            setIsExporting(false);
        }
    };
    const nationalityOptions = useMemo(() => [
        { label: t("Nationality.Swedish"), value: "Swedish" },
        { label: t("Nationality.Dutch"), value: "Dutch" },
        { label: t("Nationality.German"), value: "German" },
        { label: t("Nationality.Belgian"), value: "Belgian" },
        { label: t("Nationality.Other"), value: "Other" },
    ], [t]);
    return (_jsxs(Box, { children: [_jsx(PrimaryGrid, { columns: columns, rows: rows, noRowsText: t('events.eventDetails.transportTab.noDataAvailable'), isMultiple: true, onSelectionChange: setSelectedRows, filterOpen: gridFilterOpen, onFilterOpenChange: setGridFilterOpen, onApplyFilters: (newFilters) => {
                    setFilters(newFilters);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, customFilterRender: customFilterRender, filterOptions: {
                    nationality: nationalityOptions,
                }, resetFiltersKey: resetFiltersKey, paginationMode: 'server', rowCount: passports?.totalCount || 0, showHeaderControls: false, showNewHeaderControls: true, paginationModel: paginationModel, onPaginationModelChange: setPaginationModel, sortingMode: 'server', sortModel: sortModel, onSortModelChange: (model) => {
                    setSortModel(model);
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }, pageSizeOptions: [5, 10, 25, 50, 100], maxHeight: 'calc(100dvh - 250px)', customButton: _jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: isSmallScreen ? undefined : 'center', flexDirection: isSmallScreen ? 'column' : 'row', gap: isSmallScreen ? '12px' : 0, children: [_jsx(Typography, { variant: 'h4', children: t('events.eventDetails.passportTab.passportInformation') }), _jsxs(Box, { display: 'flex', alignItems: 'center', gap: '12px', children: [_jsx(SearchFilter, { forceDesktopVariant: true, searchValue: searchValue, onSearchChange: (value) => setSearchValue(value), placeholder: t('events.eventDetails.passportTab.search'), onClear: () => {
                                        setSearchValue('');
                                    }, showFilterClearButton: Object.keys(filters).length > 0, handleFilterClear: () => {
                                        setFilters({});
                                        setResetFiltersKey((k) => k + 1);
                                    }, widthBox: '200px', isDatagrid: true, iconButtonSize: 'medium', heightBox: '44px', onFilterClick: () => setGridFilterOpen(true), filterLabel: t('users.filters') }), selectedRows.length > 0 && (_jsx(Box, { sx: { display: 'flex', gap: 1.5, alignItems: 'center' }, children: _jsx(Button, { variant: 'outlined', color: 'primary', startIcon: _jsx(Export, { variant: 'Outline' }), onClick: () => {
                                            setExportSelection('excel');
                                            setExportDialogOpen(true);
                                        }, children: t('events.eventDetails.passportTab.exportAsExcel') }) }))] })] }), boxProps: { sx: { display: 'flex', flexDirection: 'column', gap: '12px' } } }), _jsx(PassportExportResponsibilityDialog, { open: exportDialogOpen, checked: exportConsent, onCheckedChange: setExportConsent, onClose: handleCloseExportDialog, onConfirm: handleConfirmExport })] }));
};
export default PassportContent;
export { default as PassportRestrictedAccessDialog } from './PassportRestrictedAccessDialog';
export { default as PassportOtpAuthenticationDialog } from './PassportOtpAuthenticationDialog';
export { default as PassportNonDisclosureDialog } from './PassportNonDisclosureDialog';
